import { Flex } from "@chakra-ui/layout";
import {
	type Ref,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
} from "react";
import SelectTab from "../components/inputs/SelectTab";
import SelectDateRange from "../components/inputs/SelectDateRange";
import DashboardCardWithHeader from "../components/DashboardCartWithHeader";
import type { StatsHandler } from "./StatsPage";
import { forwardRef } from "@chakra-ui/react";
import DashboardCard from "../components/DashboardCard";
import type { StatsFilterProps } from "../types/stats.types";
import { useCommandsTreatmentTimeStats } from "../hooks/stats.hooks";
import { Chart } from "chart.js";
import { CommandStatus } from "../types/command.types";

type Intervals = "week" | "month" | "year";

const GeneraleStatsTreatmentTimeBarGraph = ({
	date,
	interval,
}: StatsFilterProps) => {
	const { data: statsCommandTreatmentTime } = useCommandsTreatmentTimeStats({
		date,
		interval,
	});
	const ref = useRef(null);
	const chart = useRef<Chart | null>(null);

	const x = useMemo(() => {
		if (
			statsCommandTreatmentTime === null ||
			statsCommandTreatmentTime === undefined
		) {
			return [];
		}
		return [
			CommandStatus.RECEIVED,
			CommandStatus.MODELING,
			CommandStatus.PRINTING,
			CommandStatus.POLISHING,
			CommandStatus.READY_FOR_DELIVERY,
			CommandStatus.DELIVERED,
		].map((status) => statsCommandTreatmentTime[status].p50?.days ?? 0);
	}, [statsCommandTreatmentTime]);
	const y = useMemo(
		() => [
			"Receptionné",
			"En modélisation",
			"En impression",
			"En finition",
			"Prêt à expédier",
			"Expédié",
		],
		[],
	);

	useEffect(() => {
		if (ref.current !== null && chart.current === null) {
			chart.current = new Chart(ref.current, {
				type: "bar",
				data: {
					labels: y,
					datasets: [
						{
							data: x,
							borderWidth: 1,
							borderRadius: 4,
							backgroundColor: "#1B58F5",
						},
					],
				},
				options: {
					plugins: {
						legend: {
							display: false,
						},
						tooltip: {
							callbacks: {
								label: (context) => `${context.raw} j`,
							},
						},
					},
					scales: {
						y: {
							beginAtZero: true,
							ticks: {
								precision: 0,
								callback: (value) => `${value} j`,
							},
						},
						x: {
							grid: {
								display: false,
							},
						},
					},
					responsive: true,
					maintainAspectRatio: true,
					indexAxis: "x",
				},
			});
		}
		if (ref.current !== null && chart.current !== null) {
			const instance = chart.current;
			// X Update
			instance.data.datasets[0].data = x;
			// Y No update (static)
			// C No update (static)
			instance.update();
		}
	}, [x, y]);
	return (
		<Flex w="100%" justifyContent="center">
			<canvas ref={ref} width="100%" height="20px" />
		</Flex>
	);
};

const GeneralStatsPage = forwardRef((_: unknown, ref: Ref<StatsHandler>) => {
	const [interval, setInterval] = useState<Intervals>("year");
	const [date, setDate] = useState<Date>(new Date());
	// const [filterEnterpriseIds, _setFilterEnterpriseIds] = useState<number[]>([]);
	// const [filterCenterIds, setFilterCenterIds] = useState<number[]>([]);
	// const entreprises = useEnterprises(1, 1000);
	// const { centers } = useCenters();
	// const { data: statsCommandTreatmentTime } = useCommandsTreatmentTimeStats({
	//   date,
	//   interval,
	//   enterprises: filterEnterpriseIds,
	//   centers: filterCenterIds
	// });

	// const hasFilters = useCallback(() => {
	//   return 0 < filterCenterIds.length + filterEnterpriseIds.length;
	// }, [filterEnterpriseIds, filterCenterIds]);
	// const resetFilters = useCallback(() => {
	//   setFilterEnterpriseIds([]);
	//   setFilterCenterIds([]);
	// }, []);

	useImperativeHandle(ref, () => ({
		exportAsCsv: () => {
			return "";
		},
	}));

	return (
		<>
			<Flex justify="space-between" mt={8}>
				<SelectTab
					options={[
						{ label: "Semaine", value: "week" },
						{ label: "Mois", value: "month" },
						{ label: "Année", value: "year" },
					]}
					value={interval}
					setValue={(value) => {
						if (
							value !== undefined &&
							["week", "month", "year"].includes(value)
						) {
							setInterval(value as Intervals);
						}
					}}
				/>

				<Flex gap={4}>
					<SelectDateRange
						interval={interval}
						value={date}
						setValue={(date?: Date) => date && setDate(date)}
					/>
					{/* <FilterDrawer>
            <Flex gap={4} direction="column" pt={4}>
              <Button isDisabled={!hasFilters()} onClick={resetFilters}>
                Effacer les filtres
              </Button>

              <Flex direction="column">
                <Text>Entreprise: </Text>
                <SelectDropdown
                  preview={
                    filterEnterpriseIds.length === 0
                      ? ""
                      : `${filterEnterpriseIds.length} entreprises`
                  }
                >
                  {entreprises?.data &&
                    entreprises?.data.map((enterprise) => (
                      <SelectDropdown.Option
                        key={enterprise.id}
                        onClick={() => {
                          setFilterEnterpriseIds([
                            ...filterEnterpriseIds,
                            enterprise.id,
                          ]);
                        }}
                        active={filterEnterpriseIds.includes(enterprise.id)}
                      >
                        {enterprise.name}
                      </SelectDropdown.Option>
                    ))}
                </SelectDropdown>
              </Flex>

              <Flex direction="column">
                <Text>Centre: </Text>
                <SelectDropdown
                  preview={
                    filterCenterIds.length === 0
                      ? ""
                      : `${filterCenterIds.length} centres`
                  }
                >
                  {centers &&
                    centers.map((center) => (
                      <SelectDropdown.Option
                        key={center.id}
                        onClick={() => {
                          setFilterCenterIds([...filterCenterIds, center.id]);
                        }}
                        active={filterCenterIds.includes(center.id)}
                      >
                        {center.name}
                      </SelectDropdown.Option>
                    ))}
                </SelectDropdown>
              </Flex>
            </Flex>
          </FilterDrawer> */}
				</Flex>
			</Flex>

			<Flex mt={8} gap={4}>
				<Flex direction="column" gap={4} flexGrow={1}>
					<DashboardCard
						title="Temps de réception moyen d'empreintes physique"
						value="--"
					/>
					<DashboardCard
						title="Temps de réception moyen d'empreintes numérique"
						value="0 j"
					/>
				</Flex>

				<Flex flexGrow={1}>
					<DashboardCardWithHeader
						header="Délais de fabrication moyen"
						px="24px"
					>
						<GeneraleStatsTreatmentTimeBarGraph
							date={date}
							interval={interval}
						/>
					</DashboardCardWithHeader>
				</Flex>
			</Flex>
		</>
	);
});

export default GeneralStatsPage;
